import { useEffect, useState } from 'preact/hooks';
import type { Catalog, TVars } from './types';
import { en } from './en';

// Module-level locale store. The English catalog is compiled in and is always
// the per-key fallback; a loaded non-English catalog overlays it. Components
// render English immediately and re-render via useT when a catalog arrives —
// first paint never blocks on the fetch.
let locale = 'en';
let catalog: Catalog = {};
const listeners = new Set<() => void>();

export function getLocale(): string {
  return locale;
}

export function setCatalog(nextLocale: string, nextCatalog: Catalog): void {
  locale = nextLocale;
  catalog = nextCatalog;
  listeners.forEach((fn) => fn());
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// One PluralRules instance per locale — renders call t() dozens of times.
const pluralRules = new Map<string, Intl.PluralRules>();

function pluralRule(loc: string): Intl.PluralRules {
  let rules = pluralRules.get(loc);
  if (!rules) {
    rules = new Intl.PluralRules(loc);
    pluralRules.set(loc, rules);
  }
  return rules;
}

// t resolves a dotted catalog key to the active locale's string, falling back
// to English per key. A plural entry selects its form by the numeric `n` var
// under the active locale's plural rules; `{name}` tokens interpolate from
// vars (unknown tokens render verbatim so a publisher-style template never
// loses a placeholder to a typo'd var). A plural entry missing the selected
// form AND `other` falls through to the English entry — a translation gap can
// never render blank.
export function t(key: string, vars?: TVars): string {
  const entry = catalog[key] ?? en[key];
  if (entry == null) {
    return key;
  }
  let text: string | undefined;
  if (typeof entry === 'string') {
    text = entry;
  } else {
    const n = typeof vars?.n === 'number' ? vars.n : 1;
    text = entry[pluralRule(locale).select(n)] ?? entry.other;
  }
  if (text == null) {
    const fallback = en[key];
    text = typeof fallback === 'string' ? fallback : fallback.other;
  }
  if (!vars) {
    return text;
  }
  return text.replace(/\{(\w+)\}/g, (token, name: string) => (name in vars ? String(vars[name]) : token));
}

// useT subscribes the component to catalog loads so a mid-session arrival
// re-renders it translated. The returned function is the module-level t.
export function useT(): typeof t {
  const [, bump] = useState(0);
  useEffect(() => subscribe(() => bump((x) => x + 1)), []);
  return t;
}

// hasKey reports whether the English catalog defines the key — used to fall
// back to raw server data (e.g. an unrecognized billing interval) instead of
// rendering the key name when t() would miss.
export function hasKey(key: string): boolean {
  return en[key] != null;
}
